import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import type {
  CreditNote,
  InvoiceLine,
  InvoiceStatus,
  Money,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDate, formatMoney } from "@/modules/gst/format";
import { reasonLabel } from "@/modules/gst/reasons";

/**
 * A credit note, read-only, printed the way `InvoiceDetail` prints an
 * invoice — this is the other document a GST filing has to reconcile
 * against, so it earns the same treatment rather than a denser summary.
 *
 * Reached the same way `InvoiceDetail` is: swapped in locally by
 * `CreditNotes.tsx` when a row is clicked, or by `CreateCreditNote` once a
 * write succeeds. There is no `gst.credit-notes.detail` section in
 * `config/modules.ts`, so, like the invoice detail, this is not a route.
 *
 * `InvoicingRepository` has no `getCreditNote(id)` — only `listCreditNotes`
 * and `createCreditNote`. Taking the whole `CreditNote` as a prop rather
 * than an id sidesteps that gap entirely: `CreditNotes.tsx`'s row click
 * already holds the full row (`DataTable`'s `onRowClick` hands back the same
 * object `listCreditNotes` returned, not a projection), and a freshly
 * created note is the `WriteResult`'s own `data`. Neither caller needs a
 * fetch this repository cannot serve.
 */
export function CreditNoteDetail({
  creditNote,
  onBack,
}: {
  creditNote: CreditNote;
  onBack?: () => void;
}) {
  const data = useDataSource();

  /**
   * The invoice this note corrects, fetched the same way `InvoiceDetail`
   * fetches the order behind an invoice — through the `DataSource`, not a
   * join the fixture happens to offer. Only the invoice number is shown; the
   * note already carries its own copy of everything else (`taxKind`, `placeOfSupplyStateCode`,
   * the totals) because it must never re-derive them from an invoice that
   * could, in principle, have moved on since.
   */
  const invoiceQuery = useQuery({
    queryKey: ["gst", "credit-note-invoice", creditNote.invoiceId],
    queryFn: () => data.invoicing.get(creditNote.invoiceId),
  });

  const invoiceLabel = (): string => {
    if (invoiceQuery.data === undefined) return "Loading…";
    if (invoiceQuery.data === null) return "Not found";
    return invoiceQuery.data.invoiceNumber ?? "Not yet issued";
  };

  // The same one branch `InvoiceDetail` turns on, read off this document's
  // own `taxKind` rather than the invoice's — they happen to agree today,
  // but this note would still print correctly if the seller's registered
  // state ever changed and a much later reprint of the invoice recomputed
  // a different split. This document's split was fixed at issue.
  const isCgstSgst = creditNote.taxKind === "cgst_sgst";

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/* Optional: the drawer carries its own close. */}
      {onBack ? (
        <div className="shrink-0">
          <Button size="sm" variant="ghost" onClick={onBack}>
            <ArrowLeft />
            Back to credit notes
          </Button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto pb-8">
        <div className="bg-card mx-auto max-w-3xl space-y-6 rounded-lg border p-8 text-sm">
          <div className="flex items-start justify-between gap-6 border-b pb-4">
            <div className="space-y-1">
              <p className="text-lg font-semibold tracking-wide">CREDIT NOTE</p>
              <StatusBadge status={creditNote.status} />
            </div>
            <div className="text-right">
              <p className="font-medium">
                {creditNote.creditNoteNumber ?? "Not yet issued"}
              </p>
              <p className="text-muted-foreground">
                {creditNote.creditNoteDate
                  ? formatDate(creditNote.creditNoteDate)
                  : "—"}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Fact label="Against invoice" value={invoiceLabel()} />
            <Fact label="Reason" value={reasonLabel(creditNote.reason)} />
            <Fact
              label="Place of supply"
              value={creditNote.placeOfSupplyStateCode}
            />
            <Fact
              label="Tax type"
              value={isCgstSgst ? "CGST + SGST" : "IGST"}
            />
          </div>

          {creditNote.note ? (
            <p className="text-muted-foreground border-t pt-3">
              {creditNote.note}
            </p>
          ) : null}

          <LinesTable lines={creditNote.lines} isCgstSgst={isCgstSgst} />

          <TotalsBlock creditNote={creditNote} isCgstSgst={isCgstSgst} />
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs uppercase">{label}</p>
      <p>{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: InvoiceStatus }) {
  if (status === "issued") return <Badge>Issued</Badge>;
  if (status === "cancelled") {
    return <Badge variant="destructive">Cancelled</Badge>;
  }
  return <Badge variant="secondary">Draft</Badge>;
}

/**
 * Lines mirror `InvoiceDetail`'s `LinesTable`: the CGST/SGST-versus-IGST
 * split is two different column sets, never one set with a column zeroed.
 * A credit note's lines are the invoice's own lines at credited quantities
 * (see the comment on `CreditNote.lines`), so the same rendering applies.
 */
function LinesTable({
  lines,
  isCgstSgst,
}: {
  lines: InvoiceLine[];
  isCgstSgst: boolean;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/40">
          <tr className="border-b text-left">
            <th className="px-3 py-2 font-medium">Description</th>
            <th className="px-3 py-2 font-medium">HSN</th>
            <th className="px-3 py-2 text-right font-medium">Qty</th>
            <th className="px-3 py-2 text-right font-medium">Taxable value</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            {isCgstSgst ? (
              <>
                <th className="px-3 py-2 text-right font-medium">CGST</th>
                <th className="px-3 py-2 text-right font-medium">SGST</th>
              </>
            ) : (
              <th className="px-3 py-2 text-right font-medium">IGST</th>
            )}
            <th className="px-3 py-2 text-right font-medium">Line total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="border-b last:border-b-0">
              <td className="px-3 py-1.5">{line.description}</td>
              <td className="px-3 py-1.5 tabular-nums">{line.hsnCode}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.quantity}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {formatMoney(line.taxableValue)}
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums">
                {line.gstRate}%
              </td>
              {isCgstSgst ? (
                <>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.cgstAmount)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {formatMoney(line.sgstAmount)}
                  </td>
                </>
              ) : (
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatMoney(line.igstAmount)}
                </td>
              )}
              <td className="px-3 py-1.5 text-right font-medium tabular-nums">
                {formatMoney(line.lineTotal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TotalsBlock({
  creditNote,
  isCgstSgst,
}: {
  creditNote: CreditNote;
  isCgstSgst: boolean;
}) {
  return (
    <div className="ml-auto flex w-full max-w-xs flex-col gap-1 sm:max-w-sm">
      <TotalRow label="Taxable value" value={creditNote.totalTaxableValue} />
      {isCgstSgst ? (
        <>
          <TotalRow label="CGST" value={creditNote.totalCgst} />
          <TotalRow label="SGST" value={creditNote.totalSgst} />
        </>
      ) : (
        <TotalRow label="IGST" value={creditNote.totalIgst} />
      )}
      {Number(creditNote.totalCess) !== 0 ? (
        <TotalRow label="Cess" value={creditNote.totalCess} />
      ) : null}
      <TotalRow label="Total" value={creditNote.total} emphasis />
    </div>
  );
}

function TotalRow({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: Money;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex justify-between gap-4 tabular-nums",
        emphasis && "border-t pt-1 text-base font-semibold",
      )}
    >
      <span className={emphasis ? "" : "text-muted-foreground"}>{label}</span>
      <span>{formatMoney(value)}</span>
    </div>
  );
}
